import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatAgo } from '../../chat.format';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';

/**
 * CRM only (maqueta `Diretor.dc.html`): a faixa "Esta conversa está há 42 min
 * sem resposta" com "Assumir eu" e "Atribuir a consultor". Só markup e
 * @Outputs: quem chama `POST /chat/:chat/assign` é o contentor.
 *
 * `canTakeOver`: o próprio pode assumir (qualquer staff participante, se a
 * conversa não tem responsável; o servidor volta a verificar). `canAssign`:
 * `capabilities.canAssign` (diretor / operador / master), abre o painel.
 * Sem `since` (nada por responder) o texto é só "sem responsável".
 *
 * Botões com o desenho `btn-soft-primary` / `btn-primary` do tema.
 */
@Component({
  selector: 'chat-assign-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="chat-assign" role="status">
  <span class="chat-assign__icon"><chat-icon name="clock" [size]="16"></chat-icon></span>
  <span class="chat-assign__text">{{ text }}</span>
  <button *ngIf="canTakeOver" type="button" class="chat-assign__btn chat-assign__btn--soft" [disabled]="busy"
    (click)="takeOver.emit()">{{ strings.takeOver }}</button>
  <button *ngIf="canAssign" type="button" class="chat-assign__btn chat-assign__btn--solid" [attr.aria-expanded]="panelOpen"
    [class.chat-assign__btn--open]="panelOpen" [disabled]="busy" (click)="assign.emit()">
    <span *ngIf="busy" class="chat-assign__spinner" aria-hidden="true"></span>{{ strings.assignToConsultant }}
  </button>
</div>`,
  styles: [`
:host { display: block; flex-shrink: 0; }
.chat-assign { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 10px 16px; background: rgba(var(--chat-warning-rgb), .12); border-bottom: 1px solid rgba(var(--chat-warning-rgb), .4); color: var(--chat-warning-ink); font-size: 12.5px; animation: chat-assign-in .25s ease-out; }
.chat-assign__icon { width: 28px; height: 28px; flex-shrink: 0; border-radius: 50%; background: rgba(var(--chat-warning-rgb), .25); display: inline-flex; align-items: center; justify-content: center; }
.chat-assign__text { flex-grow: 1; min-width: 12em; }
.chat-assign__btn { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 12px; border-radius: var(--chat-radius); font: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; transition: background var(--chat-motion) ease, color var(--chat-motion) ease, box-shadow var(--chat-motion) ease; }
.chat-assign__btn[disabled] { opacity: .65; cursor: default; }
.chat-assign__btn--soft { border: 0; background: rgba(var(--chat-accent-rgb), .12); color: var(--chat-accent); }
.chat-assign__btn--soft:hover:not([disabled]) { background: var(--chat-accent); color: var(--chat-on-accent); }
.chat-assign__btn--solid { border: 0; background: var(--chat-accent); color: var(--chat-on-accent); }
.chat-assign__btn--solid:hover:not([disabled]) { filter: brightness(1.08); }
.chat-assign__btn--open { box-shadow: 0 0 0 3px rgba(var(--chat-accent-rgb), .25); }
.chat-assign__btn:focus-visible { outline: 2px solid var(--chat-ink); outline-offset: 2px; }
.chat-assign__spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(255, 255, 255, .45); border-top-color: #ffffff; animation: chat-assign-spin .7s linear infinite; }
@keyframes chat-assign-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
@keyframes chat-assign-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .chat-assign { animation: none; } .chat-assign__btn { transition: none; } }
`]
})
export class ChatAssignBannerComponent implements OnChanges {
  /** Hora da última mensagem do cliente sem resposta da equipa; null quando não há nada por responder. */
  @Input() since: any;
  /** true quando a conversa não tem consultor responsável. */
  @Input() noOwner = false;
  @Input() canTakeOver = false;
  @Input() canAssign = false;
  @Input() panelOpen = false;
  /** Um pedido de atribuição está a caminho. */
  @Input() busy = false;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  @Output() takeOver = new EventEmitter<void>();
  @Output() assign = new EventEmitter<void>();

  text = '';

  ngOnChanges(): void {
    if (!this.since) {
      this.text = this.strings.unassigned;
      return;
    }
    const ago = formatAgo(this.since, this.strings);
    const tpl = this.noOwner ? this.strings.unansweredNoOwner : this.strings.unansweredFor;
    this.text = tpl.replace('{time}', ago).replace(/\s{2,}/g, ' ');
  }
}
